import { mapActions, mapGetters } from 'vuex'
import MarketingConsent from '@/modules/buy-flow/pages/order/components/marketing-consent/index.vue'
import {
  buildMarketingConsentPayload,
  emptyMarketingConsent,
  marketingConsentChannelsFrom,
  marketingConsentFromAnswers,
} from '@/constants/marketing-consent'
import { MARKETING_CONSENTS_STATUS } from '@/store/modules/marketing-consents'

// Where a signed-in user changes their marketing decision without placing another order. The
// same four options as at checkout, from the same published `checkout_marketing` text and sent
// under its version - the journal gets new rows, the latest wins, nothing is edited.
export default {
  name: 'user-marketing-consent',
  components: {
    MarketingConsent,
  },
  data() {
    return {
      consent: emptyMarketingConsent(),
      saving: false,
    }
  },
  computed: {
    ...mapGetters({
      user: 'profile/user',
      checkoutMarketingText: 'consentTexts/checkoutMarketingText',
      version: 'consentTexts/checkoutMarketingVersion',
      answers: 'marketingConsents/marketingConsentAnswers',
      status: 'marketingConsents/marketingConsentsStatus',
    }),
    // SMS and messengers are tied to the profile phone; without one the backend has nothing to
    // record them against and answers 422, so only e-mail is offered.
    channels() {
      const channels = marketingConsentChannelsFrom(this.checkoutMarketingText)
      if (this.user && this.user.phone) return channels
      return channels.filter((channel) => channel.key === 'email')
    },
    title() {
      return (this.checkoutMarketingText && this.checkoutMarketingText.title) || ''
    },
    optOut() {
      return (this.checkoutMarketingText && this.checkoutMarketingText.optOut) || ''
    },
    // Shown only once the saved decision is known. Opening on blank boxes when the GET failed
    // would read as "you agreed to nothing", and one press of Save would journal exactly that.
    loaded() {
      return this.status === MARKETING_CONSENTS_STATUS.LOADED
    },
    saved() {
      return marketingConsentFromAnswers(this.channels, this.answers)
    },
  },
  watch: {
    saved: {
      immediate: true,
      handler(val) {
        this.consent = val
      },
    },
  },
  created() {
    this.fetchMarketingConsents()
  },
  methods: {
    ...mapActions({
      fetchMarketingConsents: 'marketingConsents/FETCH_MARKETING_CONSENTS',
      updateMarketingConsents: 'marketingConsents/UPDATE_MARKETING_CONSENTS',
      loadConsentTexts: 'consentTexts/LOAD_CONSENT_TEXTS',
    }),
    // Every channel on screen goes out, changed or not, so the journal holds the whole decision.
    async save() {
      if (this.saving) return
      this.saving = true
      try {
        await this.updateMarketingConsents(buildMarketingConsentPayload(this.consent, this.channels, this.version))
        this.$toasted.success(this.$t('successResetData'))
      } catch (error) {
        const errors = (error && error.data && error.data.errors) || null
        if (!errors) {
          this.$toasted.error((error && error.data && error.data.message) || '')
          return
        }
        // A revision was published while this page was open. Same as at checkout: no retry and
        // no invented wording - the backend's message, then fresh texts for the next attempt.
        if (errors.marketingConsentVersion) this.loadConsentTexts()
        Object.keys(errors).forEach((key) => {
          errors[key].forEach((message) => this.$toasted.error(message))
        })
      } finally {
        this.saving = false
      }
    },
  },
}
